import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { BrandsApi, ManufacturersApi, RetailerNoteApi } from '@core/api';
import { Brand, Manufacturer, NoteType } from '@shared/entities';
import { Result } from '@shared/models';
import { extractErrorMessage } from '@shared/utils';
import { commandResult, failureResult, initialListStoreState, resultErrorMessage } from './store-helpers';

type BrandInput = { manufacturerId: number; name: string };
type ManufacturerInput = { name: string };

type ReferenceCommandState = {
  commandInProgress: boolean;
  lastCommandName: string | null;
  lastCommandResult: Result<unknown> | null;
};

const initialReferenceCommandState: ReferenceCommandState = {
  commandInProgress: false,
  lastCommandName: null,
  lastCommandResult: null,
};

export const ManufacturersStore = signalStore(
  { providedIn: 'root' },
  withState({
    ...initialListStoreState<Manufacturer>(),
    ...initialReferenceCommandState,
  }),
  withComputed(({ response, loading, commandInProgress }) => ({
    records: computed(() => response().records),
    isBusy: computed(() => loading() || commandInProgress()),
    byId: computed(
      () => new Map(response().records.map((manufacturer) => [manufacturer.manufacturerId, manufacturer]))
    ),
  })),
  withMethods((store, manufacturersApi = inject(ManufacturersApi)) => {
    let loadRequestId = 0;

    const loadManufacturers = (force = false): void => {
      if (!force && store.loaded()) {
        return;
      }

      const requestId = ++loadRequestId;
      patchState(store, { loading: true, errorMessage: null });

      void manufacturersApi
        .loadManufacturers(force)
        .then((response) => {
          if (requestId === loadRequestId) {
            patchState(store, { response, loaded: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === loadRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load manufacturers.') });
          }
        })
        .finally(() => {
          if (requestId === loadRequestId) {
            patchState(store, { loading: false });
          }
        });
    };

    return {
      loadManufacturers,
      refresh(): void {
        loadManufacturers(true);
      },
      create(manufacturer: ManufacturerInput): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'create',
          lastCommandResult: null,
          errorMessage: null,
        });

        void manufacturersApi
          .create(manufacturer)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('create', result) });
            if (result.successful) {
              loadManufacturers(true);
            } else {
              patchState(store, { errorMessage: resultErrorMessage(result, 'Unable to create manufacturer.') });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to create manufacturer.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('create', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      update(id: number, manufacturer: ManufacturerInput): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'update',
          lastCommandResult: null,
          errorMessage: null,
        });

        void manufacturersApi
          .update(id, manufacturer)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('update', result) });
            if (result.successful) {
              loadManufacturers(true);
            } else {
              patchState(store, { errorMessage: resultErrorMessage(result, 'Unable to update manufacturer.') });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to update manufacturer.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('update', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      delete(id: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'delete',
          lastCommandResult: null,
          errorMessage: null,
        });

        void manufacturersApi
          .delete(id)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('delete', result) });
            if (result.successful) {
              loadManufacturers(true);
            } else {
              patchState(store, { errorMessage: resultErrorMessage(result, 'Unable to delete manufacturer.') });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to delete manufacturer.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('delete', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      clearCommandResult(): void {
        patchState(store, { lastCommandName: null, lastCommandResult: null });
      },
    };
  })
);

export const BrandsStore = signalStore(
  { providedIn: 'root' },
  withState({
    ...initialListStoreState<Brand>(),
    ...initialReferenceCommandState,
  }),
  withComputed(({ response, loading, commandInProgress }) => ({
    records: computed(() => response().records),
    isBusy: computed(() => loading() || commandInProgress()),
    byId: computed(() => new Map(response().records.map((brand) => [brand.brandId, brand]))),
  })),
  withMethods((store, brandsApi = inject(BrandsApi)) => {
    let loadRequestId = 0;

    const loadBrands = (force = false): void => {
      if (!force && store.loaded()) {
        return;
      }

      const requestId = ++loadRequestId;
      patchState(store, { loading: true, errorMessage: null });

      void brandsApi
        .loadBrands(force)
        .then((response) => {
          if (requestId === loadRequestId) {
            patchState(store, { response, loaded: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === loadRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load brands.') });
          }
        })
        .finally(() => {
          if (requestId === loadRequestId) {
            patchState(store, { loading: false });
          }
        });
    };

    return {
      loadBrands,
      refresh(): void {
        loadBrands(true);
      },
      create(brand: BrandInput): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'create',
          lastCommandResult: null,
          errorMessage: null,
        });

        void brandsApi
          .create(brand)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('create', result) });
            if (result.successful) {
              loadBrands(true);
            } else {
              patchState(store, { errorMessage: resultErrorMessage(result, 'Unable to create brand.') });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to create brand.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('create', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      update(id: number, brand: BrandInput): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'update',
          lastCommandResult: null,
          errorMessage: null,
        });

        void brandsApi
          .update(id, brand)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('update', result) });
            if (result.successful) {
              loadBrands(true);
            } else {
              patchState(store, { errorMessage: resultErrorMessage(result, 'Unable to update brand.') });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to update brand.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('update', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      delete(id: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'delete',
          lastCommandResult: null,
          errorMessage: null,
        });

        void brandsApi
          .delete(id)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('delete', result) });
            if (result.successful) {
              loadBrands(true);
            } else {
              patchState(store, { errorMessage: resultErrorMessage(result, 'Unable to delete brand.') });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to delete brand.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('delete', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      clearCommandResult(): void {
        patchState(store, { lastCommandName: null, lastCommandResult: null });
      },
    };
  })
);

export const NoteTypesStore = signalStore(
  { providedIn: 'root' },
  withState({
    ...initialListStoreState<NoteType>(),
    ...initialReferenceCommandState,
  }),
  withComputed(({ response, loading, commandInProgress }) => ({
    records: computed(() => response().records),
    isBusy: computed(() => loading() || commandInProgress()),
    byId: computed(() => new Map(response().records.map((noteType) => [noteType.noteTypeId, noteType]))),
  })),
  withMethods((store, retailerNoteApi = inject(RetailerNoteApi)) => {
    let loadRequestId = 0;

    const loadNoteTypes = (force = false): void => {
      if (!force && store.loaded()) {
        return;
      }

      const requestId = ++loadRequestId;
      patchState(store, { loading: true, errorMessage: null });

      void retailerNoteApi
        .loadNoteTypes(force)
        .then((response) => {
          if (requestId === loadRequestId) {
            patchState(store, { response, loaded: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === loadRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load note types.') });
          }
        })
        .finally(() => {
          if (requestId === loadRequestId) {
            patchState(store, { loading: false });
          }
        });
    };

    return {
      loadNoteTypes,
      refresh(): void {
        loadNoteTypes(true);
      },
      create(noteType: NoteType): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'create',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailerNoteApi
          .create(noteType)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('create', result) });
            if (result.successful) {
              loadNoteTypes(true);
            } else {
              patchState(store, { errorMessage: resultErrorMessage(result, 'Unable to create note type.') });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to create note type.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('create', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      update(id: number, noteType: NoteType): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'update',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailerNoteApi
          .update(id, noteType)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('update', result) });
            if (result.successful) {
              loadNoteTypes(true);
            } else {
              patchState(store, { errorMessage: resultErrorMessage(result, 'Unable to update note type.') });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to update note type.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('update', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      delete(id: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'delete',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailerNoteApi
          .delete(id)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('delete', result) });
            if (result.successful) {
              loadNoteTypes(true);
            } else {
              patchState(store, { errorMessage: resultErrorMessage(result, 'Unable to delete note type.') });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to delete note type.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('delete', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      clearCommandResult(): void {
        patchState(store, { lastCommandName: null, lastCommandResult: null });
      },
    };
  })
);
